import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UserPlus, CheckCircle2, AlertTriangle, Fingerprint } from "lucide-react";
import { api, btnGhost, fmtErr, input } from "./shared";

const STATUS = {
  complete: ["Complete", "text-emerald-300 border-emerald-500/40"],
  pending: ["Pending — ID pushed, biometrics not yet captured", "text-amber-300 border-amber-500/40"],
  not_enrolled: ["Not Enrolled", "text-slate-400 border-white/15"],
};

export const EnrolStaff = ({ canEdit }) => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {name, items:[{sn,device_name,status,error}]}
  const timer = useRef(null);
  const load = useCallback(() => api.get("/biometric/enrolments").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearInterval(timer.current), []);

  const remote = (data?.devices || []).filter((d) => d.remote);
  const hik = (data?.devices || []).find((d) => !d.remote);
  const staff = (data?.rows || []).filter((r) => !r.is_ceo && r.employee_id);
  const rows = (data?.rows || []).filter((r) => r.is_ceo || r.employee_id);
  const sel = staff.find((r) => r.email === email);

  const poll = (em, name, started) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data: s } = await api.get(`/biometric/enrol/status?email=${encodeURIComponent(em)}`);
        const items = s.items.filter((i) => i.at >= started);
        setResult({ name, items });
        if (items.length && items.every((i) => i.status !== "pushed")) { clearInterval(timer.current); load(); }
      } catch {}
    }, 4000);
  };

  const enrol = async () => {
    const devices = Object.keys(picked).filter((k) => picked[k]);
    if (!sel || !devices.length) return toast.error("Pick a staff member and at least one device");
    setBusy(true);
    try {
      const started = new Date().toISOString();
      const { data: r } = await api.post("/biometric/enrol", { email, devices });
      toast.success(`Name + ID sent to ${devices.length} device(s) — waiting for acknowledgement`);
      setResult({ name: r.name, items: r.pushed.map((p) => ({ sn: p.sn, status: "pushed", device_name: remote.find((d) => d.sn === p.sn)?.name || p.sn })) });
      poll(email, r.name, started.slice(0, 19));
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4 sm:p-5" data-testid="enrol-staff-panel">
      <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2 mb-3"><UserPlus size={13} className="text-nx-goldlight" /> Enroll staff on device</p>
      {canEdit && (
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={email} onChange={(e) => { setEmail(e.target.value); setResult(null); }} className={`${input} sm:w-72 !bg-[#0b1220] text-slate-100`} style={{ backgroundColor: "#0b1220", colorScheme: "dark" }} data-testid="enrol-staff-select">
              <option value="" className="bg-[#0b1220] text-slate-100">Select staff member…</option>
              {staff.map((r) => <option key={r.email} value={r.email} className="bg-[#0b1220] text-slate-100">{r.name} · {r.employee_id}</option>)}
            </select>
            <div className="flex flex-wrap items-center gap-3">
              {remote.map((d) => (
                <label key={d.sn} className="flex items-center gap-2 text-xs text-slate-200 border border-white/15 px-3 py-2.5 cursor-pointer" data-testid={`enrol-device-${d.sn}`}>
                  <input type="checkbox" checked={!!picked[d.sn]} onChange={(e) => setPicked((p) => ({ ...p, [d.sn]: e.target.checked }))} className="accent-nx-gold" />
                  {d.name} <span className="text-slate-500 font-mono text-[10px]">{d.sn}</span>
                </label>
              ))}
              <button onClick={enrol} disabled={busy || !sel || !Object.values(picked).some(Boolean)} className="bg-nx-gold hover:bg-nx-goldlight disabled:opacity-50 text-nx-navy font-semibold text-xs tracking-[0.12em] uppercase px-4 py-2.5" data-testid="enrol-submit">
                {busy ? "Sending…" : "Enroll"}
              </button>
            </div>
          </div>
          {hik && <p className="text-[11px] text-slate-500" data-testid="enrol-hik-note">Hikvision enrollment must be done directly on the device ({hik.name}).</p>}
        </div>
      )}
      {result && (
        <div className="flex flex-col gap-2 mb-4" data-testid="enrol-result">
          {result.items.map((i) => (
            <div key={i.sn} className={`text-sm px-3 py-2.5 border flex items-start gap-2 ${i.status === "failed" ? "border-red-500/50 bg-red-500/10 text-red-200" : i.status === "pushed" ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-emerald-500/50 bg-emerald-500/10 text-emerald-200"}`} data-testid={`enrol-result-${i.sn}`}>
              {i.status === "failed" ? <AlertTriangle size={16} className="mt-0.5 shrink-0" /> : <CheckCircle2 size={16} className="mt-0.5 shrink-0" />}
              <span>
                {i.status === "failed" ? `Enrollment of ${result.name} on ${i.device_name} failed — ${i.error || "no acknowledgement"}. Please check device connectivity.`
                  : i.status === "pushed" ? `Waiting for ${i.device_name} to acknowledge (up to 2 minutes)…`
                  : `${result.name} enrolled on ${i.device_name} successfully.`}
              </span>
            </div>
          ))}
          {result.items.some((i) => i.status === "acked" || i.status === "complete") && (
            <p className="text-sm text-slate-200 border border-nx-gold/40 bg-nx-gold/5 px-3 py-2.5 flex items-start gap-2" data-testid="enrol-instruction">
              <Fingerprint size={16} className="mt-0.5 shrink-0 text-nx-goldlight" />
              <span>Staff name and ID have been sent to the device. Ask <strong>{result.name}</strong> to visit the device physically to register their fingerprint and face.</span>
            </p>
          )}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[640px]" data-testid="enrol-matrix">
          <thead><tr className="text-left text-slate-500"><th className="py-2 pr-3">Staff</th>{data.devices.map((d) => <th key={d.sn} className="pr-3">{d.name}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.email} className="border-t border-white/5" data-testid={`enrol-row-${r.email}`}>
                <td className="py-2 pr-3"><span className="text-white">{r.name}</span>{r.is_ceo && <span className="ml-1 text-[10px] text-nx-goldlight">CEO</span>}<span className="block text-[10px] text-slate-500 font-mono">{r.employee_id || "no Employee ID"}</span></td>
                {data.devices.map((d) => {
                  const cell = r.devices[d.sn] || {};
                  const [label, cls] = STATUS[cell.status] || STATUS.not_enrolled;
                  return (
                    <td key={d.sn} className="pr-3 py-2">
                      <span className={`inline-block border px-1.5 py-0.5 text-[10px] tracking-[0.08em] uppercase ${cls}`} data-testid={`enrol-status-${r.email}-${d.sn}`}>{r.is_ceo ? "Complete" : label}</span>
                      {!r.is_ceo && cell.uids?.length > 0 && <span className="block text-[10px] text-slate-500 font-mono mt-0.5">ID {cell.uids.join(", ")}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!canEdit && <p className="text-[11px] text-slate-500 mt-2">Only the CEO or HR can enroll staff.</p>}
    </div>
  );
};
